import { useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Loader2, MapPin } from "lucide-react";

// Type-ahead US address search backed by /public/address-suggest (OpenStreetMap).
// onSelect receives { address, city, state, zip, lat, lng } for a verified result.
export default function AddressAutocomplete({ value, onChange, onSelect, placeholder, testId = "address", className }) {
  const [sugs, setSugs] = useState([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const skipRef = useRef(false);
  const boxRef = useRef(null);

  useEffect(() => {
    if (skipRef.current) { skipRef.current = false; return; }
    const q = (value || "").trim();
    if (q.length < 3) { setSugs([]); setOpen(false); return; }
    setLoading(true);
    const t = setTimeout(async () => {
      try {
        const { data } = await api.get("/public/address-suggest", { params: { q } });
        setSugs(data.suggestions || []);
        setOpen((data.suggestions || []).length > 0);
      } catch { setSugs([]); }
      finally { setLoading(false); }
    }, 350);
    return () => clearTimeout(t);
  }, [value]);

  useEffect(() => {
    const onDoc = (e) => { if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);

  const pick = (s) => { skipRef.current = true; onSelect(s); setOpen(false); setSugs([]); };

  return (
    <div className="relative" ref={boxRef}>
      <Input value={value} onChange={(e) => onChange(e.target.value)} onFocus={() => sugs.length && setOpen(true)}
        placeholder={placeholder} autoComplete="off" data-testid={testId} className={className} />
      {loading && <Loader2 className="w-4 h-4 animate-spin absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground" />}
      {open && sugs.length > 0 && (
        <div className="absolute z-50 mt-1 w-full rounded-lg border border-border bg-popover shadow-lg max-h-64 overflow-y-auto" data-testid={`${testId}-suggestions`}>
          {sugs.map((s, i) => (
            <button type="button" key={i} onClick={() => pick(s)} data-testid={`${testId}-suggestion-${i}`}
              className="w-full text-left px-3 py-2 text-sm hover:bg-accent flex items-start gap-2 border-b border-border last:border-0">
              <MapPin className="w-3.5 h-3.5 mt-0.5 shrink-0 text-primary" />
              <span className="truncate">{s.label}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
